import * as React from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { clean } from "@/components/Pagination"
import type { RunFilterOptions, RunFilterValues } from "@/types/run"

const RESULTS = [
  { value: "all", label: "Any result" },
  { value: "completed", label: "Completed" },
  { value: "with_failures", label: "Had failures" },
  { value: "refused", label: "Refused" },
  { value: "failed", label: "Failed" },
]

const KINDS = [
  { value: "all", label: "Live sends and rehearsals" },
  { value: "real", label: "Live sends only" },
  { value: "dry", label: "Rehearsals only" },
]

export function RunFilters({
  filters,
  options,
}: {
  filters: RunFilterValues
  options: RunFilterOptions
}) {
  // Filtering always returns to page one — staying on page 7 of a list that
  // just shrank to two pages is never what someone meant.
  const apply = (changes: Partial<Record<string, string | number | null>>) => {
    router.get(
      "/runs",
      clean({
        automation_id: filters.automation_id,
        from: filters.from,
        to: filters.to,
        result: filters.result === "all" ? null : filters.result,
        kind: filters.kind === "all" ? null : filters.kind,
        organization: filters.organization === "all" ? "all" : null,
        ...changes,
      }),
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }

  const isFiltered =
    filters.automation_id !== null ||
    Boolean(filters.from) ||
    Boolean(filters.to) ||
    filters.result !== "all" ||
    filters.kind !== "all" ||
    filters.organization === "all"

  return (
    <div className="callout mb-6 py-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Automation" htmlFor="filter_automation">
          <Select
            id="filter_automation"
            value={filters.automation_id ?? ""}
            onChange={(event) =>
              apply({ automation_id: event.target.value || null })
            }
          >
            <option value="">All automations</option>
            {options.automations.map((automation) => (
              <option key={automation.id} value={automation.id}>
                {automation.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Result" htmlFor="filter_result">
          <Select
            id="filter_result"
            value={filters.result}
            onChange={(event) => apply({ result: event.target.value })}
          >
            {RESULTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Type" htmlFor="filter_kind">
          <Select
            id="filter_kind"
            value={filters.kind}
            onChange={(event) => apply({ kind: event.target.value })}
          >
            {KINDS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="From" htmlFor="filter_from">
          <Input
            id="filter_from"
            type="date"
            value={filters.from ?? ""}
            onChange={(event) => apply({ from: event.target.value || null })}
          />
        </Field>

        <Field label="To" htmlFor="filter_to">
          <Input
            id="filter_to"
            type="date"
            value={filters.to ?? ""}
            onChange={(event) => apply({ to: event.target.value || null })}
          />
        </Field>

        {/* IUPAT staff only. A District Council is pinned to its own history. */}
        {options.can_see_all_organizations && (
          <Field label="Organization" htmlFor="filter_organization">
            <Select
              id="filter_organization"
              value={filters.organization}
              onChange={(event) =>
                apply({
                  organization: event.target.value === "all" ? "all" : null,
                })
              }
            >
              <option value="current">{options.organization_name} only</option>
              <option value="all">All District Councils</option>
            </Select>
          </Field>
        )}
      </div>

      {isFiltered && (
        <div className="mt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              router.get(
                "/runs",
                {},
                { preserveState: true, preserveScroll: true, replace: true },
              )
            }
          >
            Clear filters
          </Button>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor}>{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  )
}
